/**
 * CombatantListItemContainer - Container for individual combatant rows.
 *
 * Follows ADR-034 Container/Presenter pattern:
 * - Connects to Zustand store via useActions for combat mutations
 * - Wraps each handler in useStableCallback for stable references
 * - Passes all data and callbacks as props to CombatantListItem presenter
 *
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { useActions } from '../../hooks/useStore';
import { useStableCallback } from '../../hooks/hookUtils';
import CombatantListItem from './CombatantListItem';
import type { Combatant } from '../../types/types';

interface CombatantListItemContainerProps {
  combatant: Combatant;
  isActiveTurn: boolean;
  isNextTurn: boolean;
  setupMode: boolean;
  imageMap: Map<number, string>;
}

/**
 * Container component for CombatantListItem.
 *
 * Reads actions from the store and provides stable handler callbacks
 * to the pure CombatantListItem presenter. The combatant data itself
 * is received as a prop from CombatantListContainer.
 */
const CombatantListItemContainer: React.FC<CombatantListItemContainerProps> = ({
  combatant,
  isActiveTurn,
  isNextTurn,
  setupMode,
  imageMap,
}) => {
  const actions = useActions();

  // Wrap each handler in useStableCallback so references never change,
  // preventing unnecessary re-renders of child elements like InputNumber and Tag.
  const handleHpChange = useStableCallback((value: number | null) => {
    actions.updateCombatant({ id: combatant.id, hp: value ?? 0 });
  });

  const handleInitiativeChange = useStableCallback((value: number | null) => {
    actions.updateCombatant({ id: combatant.id, initiative: value ?? 0 });
  });

  const handleRemoveCombatant = useStableCallback(() => {
    actions.removeCombatant(combatant.id);
  });

  const handleAddStatus = useStableCallback((status: string) => {
    actions.addStatusEffect(combatant.id, status);
  });

  const handleRemoveStatus = useStableCallback((status: string) => {
    actions.removeStatusEffect(combatant.id, status);
  });

  return (
    <CombatantListItem
      combatant={combatant}
      isActiveTurn={isActiveTurn}
      isNextTurn={isNextTurn}
      setupMode={setupMode}
      imageMap={imageMap}
      onHpChange={handleHpChange}
      onInitiativeChange={handleInitiativeChange}
      onRemoveCombatant={handleRemoveCombatant}
      onAddStatus={handleAddStatus}
      onRemoveStatus={handleRemoveStatus}
    />
  );
};

export default CombatantListItemContainer;
